import React, { memo, useCallback, useMemo } from 'react';
import './OpportunityCard.scss';

function getPayDisplay(item) {
  const payTypes = Array.isArray(item.payType)
    ? item.payType
    : (item.payType ? [item.payType] : []);
  const hasPaid = payTypes.some(
    type => typeof type === 'string' && type.toLowerCase().includes('paid')
  );

  if (hasPaid) {
    return item.payDescription ? item.payDescription : 'Paid';
  }
  if (item.payDescription && !/no compensation/i.test(item.payDescription)) {
    return item.payDescription;
  }
  return 'Volunteer';
}

function getOpenPositionsBadge(openPositions) {
  return {
    label: `${openPositions} open`,
    className: 'card-badge card-badge--open-positions',
  };
}


function OpportunityCard({ item }) {
  const openPositions = useMemo(() => {
    if (item.openPositions === null || item.openPositions === undefined) return '0';
    const value = String(item.openPositions).trim();
    return value || '0';
  }, [item.openPositions]);
  const focusArea = useMemo(
    () => (item.focusAreas && item.focusAreas.length > 0 ? `#${item.focusAreas[0]}` : ''),
    [item.focusAreas]
  );
  const ageRange = useMemo(
    () => (Array.isArray(item.ageRange) ? item.ageRange.join(', ') : (item.ageRange || '')),
    [item.ageRange]
  );
  const badge = useMemo(() => getOpenPositionsBadge(openPositions), [openPositions]);
  const pay = useMemo(() => getPayDisplay(item), [item]);


  // Use pdfUrl as the main link if present, otherwise applicationUrl
  const mainUrl = item.pdfUrl || item.applicationUrl;
  const openApplication = useCallback(() => {
    if (mainUrl) window.open(mainUrl, item.openInNewWindow ? '_blank' : '_self');
  }, [mainUrl, item.openInNewWindow]);

  const handleCardClick = useCallback((e) => {
    // Only trigger if not clicking a link or button inside
    if (e.target.closest('.btn-apply')) return;
    openApplication();
  }, [openApplication]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openApplication();
    }
  }, [openApplication]);

  return (
    <article
      className="card card-clickable"
      tabIndex={0}
      aria-label={`${item.title}. Press Enter to learn more.`}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
    >
      <div className="card-top">
        <div className="card-title-group">
          <h2 className="card-title">{item.title}</h2>
          {ageRange && <p className="card-age-range">{`${ageRange} Years Old`}</p>}
          <p className="card-category">{focusArea}</p>
        </div>
        <div className="card-badges">
          <span className={badge.className}>{badge.label}</span>
        </div>
      </div>
      <p className="card-pay">{pay}</p>
      <p className="card-location"><span aria-hidden="true">@</span>{item.location}</p>
      <a
        className="btn-apply"
        href={mainUrl}
        tabIndex={-1}
        aria-hidden="true"
        onClick={e => { e.stopPropagation(); }}
        {...(item.openInNewWindow ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        LEARN MORE
      </a>
    </article>
  );
}

export default memo(OpportunityCard);
